import { Button, Dialog, IconButton, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { IssuePriorityType, type IssueTypePriority } from "@overmap-ai/common"
import { useForm } from "@tanstack/react-form"
import { type FormEventHandler, memo, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { ColorButton } from "../Buttons/ColorButton"
import { ColorPickerPopover } from "../Popovers/ColorPickerPopover"
import type { ExtendableDialogProps } from "./typings"

interface CreateIssueTypePriorityDialogProps extends Omit<ExtendableDialogProps, "onSubmit"> {
	onSubmit: (values: Pick<IssueTypePriority, "color" | "name" | "type">) => void
	defaultColor: string
	priorityType: IssuePriorityType
}

export const CreateIssueTypePriorityDialog = memo<CreateIssueTypePriorityDialogProps>((props) => {
	const { children, open, defaultOpen, onOpenChange, modal, onSubmit, defaultColor, priorityType, ...rest } = props

	const form = useForm({
		defaultValues: {
			name: "",
			color: defaultColor,
		},
		onSubmit: ({ value, formApi }) => {
			onSubmit({ ...value, type: priorityType })
			formApi.reset()
		},
	})

	const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()
			void form.handleSubmit()
		},
		[form],
	)

	return (
		<Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay />
			<Dialog.Content className="flex flex-col" {...rest}>
				<div className="flex justify-between gap-2 pb-2">
					<Dialog.Title>
						<Text size="lg">Create priority</Text>
					</Dialog.Title>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>

				<Separator size="full" />

				<form className="mt-2 flex w-full flex-col gap-4" onSubmit={handleSubmit}>
					<form.Field
						name="name"
						validators={{
							onBlur: ({ value }) => (!value ? "Name is required" : undefined),
						}}
					>
						{(field) => (
							<div className="flex w-full flex-col gap-1">
								<label htmlFor={field.name}>
									<Text>Name</Text>
								</label>

								<Input.Root
									variant="outline"
									className="!bg-(--base-a2) w-full"
									accentColor={!field.state.meta.isValid ? "danger" : "primary"}
								>
									<Input.Field
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
								</Input.Root>

								{!field.state.meta.isValid && (
									<Text size="sm" weight="regular" accentColor="danger">
										{field.state.meta.errors.join(",")}
									</Text>
								)}
							</div>
						)}
					</form.Field>

					<form.Field
						name="color"
						validators={{
							onBlur: ({ value }) => (!value ? "Color is required" : undefined),
						}}
					>
						{(field) => (
							<div className="flex w-full flex-col gap-1">
								<label>
									<Text>Color</Text>
								</label>

								<ColorPickerPopover
									required
									color={field.state.value}
									onColorChange={(color) => field.handleChange(color)}
									size="sm"
								>
									<ColorButton color={field.state.value} size="md" radius="full" />
								</ColorPickerPopover>

								{!field.state.meta.isValid && (
									<Text size="sm" weight="regular" accentColor="danger">
										{field.state.meta.errors.join(",")}
									</Text>
								)}
							</div>
						)}
					</form.Field>

					<form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
						{([canSubmit]) => (
							<Dialog.Close asChild>
								<Button type="submit" disabled={!canSubmit} variant="surface">
									Create
								</Button>
							</Dialog.Close>
						)}
					</form.Subscribe>

					<form.Subscribe selector={(state) => [state.errorMap]}>
						{([errorMap]) =>
							!!errorMap.onSubmit && (
								<Text accentColor="danger" size="xs">
									{errorMap.onSubmit}
								</Text>
							)
						}
					</form.Subscribe>
				</form>
			</Dialog.Content>
		</Dialog.Root>
	)
})
CreateIssueTypePriorityDialog.displayName = "CreateIssueTypePriorityDialog"
